import type { PaginationData } from '../types/store'

/**
 * Reconcile the listing's headline total with the category-facet count.
 *
 * On a plain category page the "Wyświetlam … z N produktów" header is fed by the
 * ElasticJetSearch fast-path (`/categories/products/[id]`, `skip_filters=1`), while the
 * product count shown next to the current category in the filter tree comes from the
 * Amazzing read-model (`/categories/filters/[id]`, `contelizer_af_index_category` +
 * live `stock_available.quantity > 0`). Those are two independent engines/indexes, so
 * their counts drift by small amounts (a product flips stock state between the ES reindex
 * snapshot and the live DB row). The result is "z 1723" in the header but "1722" in the
 * filter — confusing even when it is only off by one.
 *
 * When the read-model total is available (client-side, no active semantic filters), use it
 * for the headline so the two numbers agree. `from`/`to` are clamped to the new total so the
 * last page never reads "1681–1723 z 1722". Navigation fields (`totalPages`, `pages`) are left
 * untouched: ElasticJetSearch actually serves the product grid, so page links must match it.
 *
 * Returns the base reference unchanged when there is nothing to reconcile (no base, no/invalid
 * read-model total, or the totals already agree) — keeping SSR and the semantic-filter path on
 * the Elastic total, since the read-model filters query does not run there.
 */
export function reconcileListingTotal(
  base: PaginationData | null | undefined,
  readModelTotalItems: number | null | undefined,
): PaginationData | null | undefined {
  if (!base) {
    return base
  }

  if (
    typeof readModelTotalItems !== 'number'
    || !Number.isFinite(readModelTotalItems)
    || readModelTotalItems < 0
    || readModelTotalItems === base.totalItems
  ) {
    return base
  }

  return {
    ...base,
    totalItems: readModelTotalItems,
    from: Math.min(base.from, readModelTotalItems),
    to: Math.min(base.to, readModelTotalItems),
  }
}
